import { AuthorizationService, ContentId, DataVersion, ProblemHeaderId } from '@/base/domains';
import {
  AbstractUseCase,
  assertDataVersion,
  assertEntityExists,
  UseCase,
  UseCaseResponse,
} from '@/base/usecases';
import { injectionKeyOf, requiredInject } from '@/utils/VueUtils';

import { EditingConfirmedContentReference, EditingConfirmedContentRepository } from '../domains';

/**
 * 編集中確定済みコンテンツ練習問題の問題ヘッダーを削除する
 */
export type RemoveProblemHeaderFromEditingConfirmedContentWorkbookRequest = {
  id: ContentId;
  problemHeaderId: ProblemHeaderId;
  expectedDataVersion: DataVersion;
};

export type RemoveProblemHeaderFromEditingConfirmedContentWorkbookResponse = {
  editingConfirmedContent: EditingConfirmedContentReference;
};

export interface RemoveProblemHeaderFromEditingConfirmedContentWorkbook
  extends UseCase<
    RemoveProblemHeaderFromEditingConfirmedContentWorkbookRequest,
    RemoveProblemHeaderFromEditingConfirmedContentWorkbookResponse
  > {
  execute(
    request: RemoveProblemHeaderFromEditingConfirmedContentWorkbookRequest
  ): Promise<UseCaseResponse<RemoveProblemHeaderFromEditingConfirmedContentWorkbookResponse>>;
}

export class RemoveProblemHeaderFromEditingConfirmedContentWorkbookImpl
  extends AbstractUseCase<
    RemoveProblemHeaderFromEditingConfirmedContentWorkbookRequest,
    RemoveProblemHeaderFromEditingConfirmedContentWorkbookResponse
  >
  implements RemoveProblemHeaderFromEditingConfirmedContentWorkbook
{
  constructor(
    private authorizationService: AuthorizationService,
    private editingConfirmedContentRepository: EditingConfirmedContentRepository
  ) {
    super('contents.RemoveProblemHeaderFromEditingConfirmedContentWorkbook');
  }

  async internalExecute(
    request: RemoveProblemHeaderFromEditingConfirmedContentWorkbookRequest
  ): Promise<RemoveProblemHeaderFromEditingConfirmedContentWorkbookResponse> {
    const { id, problemHeaderId, expectedDataVersion } = request;
    this.authorizationService.assertContentEditable();
    const content = await this.editingConfirmedContentRepository.findById(id);
    assertEntityExists(content, 'editingConfirmedContent');
    assertDataVersion(content, expectedDataVersion, 'editingConfirmedContent');
    if (content.type === 'text') {
      const saved = await this.editingConfirmedContentRepository.save(
        content.removeWorkbookProblemHeader(problemHeaderId)
      );
      return {
        editingConfirmedContent: saved,
      };
    }
    throw new Error('editingConfirmedContent.type should be text');
  }
}

export const RemoveProblemHeaderFromEditingConfirmedContentWorkbookKey =
  injectionKeyOf<RemoveProblemHeaderFromEditingConfirmedContentWorkbook>({
    boundedContext: 'contents',
    type: 'usecase',
    name: 'RemoveProblemHeaderFromEditingConfirmedContentWorkbook',
  });

export function useRemoveProblemHeaderFromEditingConfirmedContentWorkbook(): RemoveProblemHeaderFromEditingConfirmedContentWorkbook {
  return requiredInject(RemoveProblemHeaderFromEditingConfirmedContentWorkbookKey);
}
